import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Mic, MicOff, Send, Plus, Trash2, Download, Loader2, Edit3, Lock, Save, Settings, FileText, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InternalQuote = () => {
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState('');
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [inputText, setInputText] = useState('');
  const [items, setItems] = useState([]);
  const [modifications, setModifications] = useState([]);
  const [projectName, setProjectName] = useState('');
  const [projectAddress, setProjectAddress] = useState('');
  const [detectedRegion, setDetectedRegion] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [showPrices, setShowPrices] = useState(false);
  const [prices, setPrices] = useState([]);
  const [newPrice, setNewPrice] = useState({ name: '', category: '', unit_price: '', unit: 'each', description: '', region: '' });
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const printRef = useRef(null);

  useEffect(() => { if (authed) loadPrices(); }, [authed]);

  // NorCal counties/cities
  const NORCAL_KEYWORDS = ['san francisco','san jose','oakland','sacramento','san mateo','santa clara','alameda','contra costa','marin','sonoma','napa','solano','santa cruz','monterey','fresno','stockton','modesto','fremont','hayward','sunnyvale','palo alto','mountain view','berkeley','richmond','vallejo','concord','san rafael','redwood city','milpitas','cupertino','campbell','los gatos','saratoga','gilroy','morgan hill','pleasanton','livermore','dublin','walnut creek','danville','san ramon','tracy','manteca','turlock','merced','visalia','bakersfield','san luis obispo'];
  const detectRegion = (addr) => {
    const lower = (addr || '').toLowerCase();
    for (const kw of NORCAL_KEYWORDS) { if (lower.includes(kw)) return '北加'; }
    if (lower.includes('california') || lower.includes(', ca')) {
      return '南加';
    }
    return '';
  };

  const handleAddressChange = (val) => {
    setProjectAddress(val);
    const region = detectRegion(val);
    setDetectedRegion(region);
  };

  const loadPrices = async () => {
    try { const res = await axios.get(`${API}/internal/prices`); setPrices(res.data.items || []); } catch {}
  };

  const handleLogin = async () => {
    try {
      const res = await axios.post(`${API}/internal/auth`, { password });
      if (res.data.status === 'ok') { setAuthed(true); toast.success('Access granted'); }
      else toast.error('Invalid password');
    } catch { toast.error('Login failed'); }
  };

  // ===== VOICE RECORDING =====
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(blob);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch { toast.error('Microphone access denied'); }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const transcribeAudio = async (blob) => {
    setTranscribing(true);
    try {
      const form = new FormData();
      form.append('file', blob, 'recording.webm');
      const res = await axios.post(`${API}/internal/transcribe`, form);
      if (res.data.status === 'ok') {
        setInputText(prev => prev ? `${prev} ${res.data.text}` : res.data.text);
        toast.success('Voice transcribed');
      } else toast.error(res.data.message);
    } catch { toast.error('Transcription failed'); }
    finally { setTranscribing(false); }
  };

  // ===== AI PARSE =====
  const handleParse = async () => {
    if (!inputText.trim()) { toast.error('Please enter or speak a description'); return; }
    setParsing(true);
    try {
      const regionParam = detectedRegion === '北加' ? '北加' : detectedRegion === '南加' ? '南加' : '';
      const res = await axios.post(`${API}/internal/parse-quote`, { text: inputText, region: regionParam });
      if (res.data.status === 'ok' && res.data.data?.items) {
        setItems(res.data.data.items.map((it, i) => ({ ...it, id: Date.now() + i })));
        if (res.data.data.notes) setNotes(res.data.data.notes);
        toast.success(`Parsed ${res.data.data.items.length} items`);
      } else toast.error('Parse failed');
    } catch { toast.error('Parse failed'); }
    finally { setParsing(false); }
  };

  // ===== ITEM EDITING =====
  const updateItem = (id, field, value) => {
    setItems(prev => prev.map(it => {
      if (it.id !== id) return it;
      const updated = { ...it, [field]: value };
      if (field === 'unit_price' || field === 'quantity') {
        updated.subtotal = (parseFloat(updated.unit_price) || 0) * (parseFloat(updated.quantity) || 0);
      }
      // Track modification
      if (it.is_standard && (field === 'unit_price' || field === 'name')) {
        const mod = { item: it.name, field, original: it[field], modified: value, time: new Date().toISOString() };
        setModifications(prev => [...prev, mod]);
      }
      return updated;
    }));
  };

  const removeItem = (id) => setItems(prev => prev.filter(it => it.id !== id));

  const addItem = () => {
    setItems(prev => [...prev, { id: Date.now(), name: '', description: '', unit_price: 0, quantity: 1, unit: 'each', subtotal: 0, is_standard: false }]);
  };

  const total = items.reduce((s, it) => s + (parseFloat(it.subtotal) || 0), 0);

  // ===== ADD PRICE =====
  const handleAddPrice = async () => {
    if (!newPrice.name || !newPrice.unit_price) { toast.error('Name and price required'); return; }
    try {
      await axios.post(`${API}/internal/prices`, { ...newPrice, unit_price: parseFloat(newPrice.unit_price) });
      setNewPrice({ name: '', category: '', unit_price: '', unit: 'each', description: '', region: '' });
      loadPrices();
      toast.success('Price added');
    } catch { toast.error('Failed'); }
  };

  // ===== EXPORT PDF =====
  const handlePdf = async () => {
    if (items.length === 0) { toast.error('No items to export'); return; }
    // Create a temporary visible element for html2canvas
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '0';
    document.body.appendChild(container);

    const el = printRef.current.cloneNode(true);
    el.style.display = 'block';
    container.appendChild(el);

    try {
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#fff', width: 800 });
      const pdf = new jsPDF('p', 'mm', 'a4');
      const w = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const ratio = w / canvas.width;
      const h = canvas.height * ratio;
      const imgData = canvas.toDataURL('image/png');
      let pos = 0, left = h;
      pdf.addImage(imgData, 'PNG', 0, pos, w, h);
      left -= pageH;
      while (left > 0) { pos -= pageH; pdf.addPage(); pdf.addImage(imgData, 'PNG', 0, pos, w, h); left -= pageH; }
      pdf.save(`ALIGCUS_Quote_${projectName || 'draft'}.pdf`);
      toast.success('PDF exported');
    } catch (e) {
      console.error('PDF error:', e);
      toast.error('PDF export failed');
    } finally {
      document.body.removeChild(container);
    }
  };

  // ===== LOGIN SCREEN =====
  if (!authed) return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <Card className="w-full max-w-md shadow-2xl">
        <CardContent className="p-8">
          <div className="text-center mb-6">
            <img src="https://customer-assets.emergentagent.com/job_9ca81a2a-0a4d-4b76-b02e-3997c1e64651/artifacts/ypha2bz9_%E5%BE%AE%E4%BF%A1%E5%9B%BE%E7%89%87_20260309130435_160_131.png" alt="ALIGCUS" className="h-16 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900">Internal Quote System</h1>
            <p className="text-sm text-gray-500">AI-Powered Voice Quoting</p>
          </div>
          <div className="space-y-4">
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-3.5 text-gray-400" />
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleLogin(); }}
                placeholder="Enter access password" className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:border-red-600 outline-none" data-testid="internal-password" />
            </div>
            <Button onClick={handleLogin} className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3" data-testid="internal-login">
              Access System
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // ===== MAIN SYSTEM =====
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gray-900 text-white py-3 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_9ca81a2a-0a4d-4b76-b02e-3997c1e64651/artifacts/ypha2bz9_%E5%BE%AE%E4%BF%A1%E5%9B%BE%E7%89%87_20260309130435_160_131.png" alt="" className="h-8" />
            <h1 className="font-bold text-lg">AI Voice Quote System</h1>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowPrices(!showPrices)} variant="outline" className="border-white/30 text-white hover:bg-white/10 text-sm">
              <Settings className="w-4 h-4 mr-1" /> Price List
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Price Management Panel */}
        {showPrices && (
          <Card className="mb-6 shadow-lg">
            <CardContent className="p-4">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2"><Settings className="w-4 h-4" /> Price Items Management</h3>
              <div className="overflow-x-auto max-h-60 overflow-y-auto mb-3">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0"><tr>
                    <th className="p-2 text-left">Name</th><th className="p-2 text-left">Category</th><th className="p-2 text-left">Region</th><th className="p-2 text-right">Price</th><th className="p-2 text-left">Unit</th><th className="p-2">Action</th>
                  </tr></thead>
                  <tbody className="divide-y">
                    {prices.map((p, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="p-2 font-medium">{p.name}</td><td className="p-2 text-gray-500">{p.category}</td>
                        <td className="p-2"><span className={`text-xs px-1.5 py-0.5 rounded ${p.region === '北加' ? 'bg-blue-100 text-blue-700' : p.region === '南加' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500'}`}>{p.region || 'All'}</span></td>
                        <td className="p-2 text-right font-bold">${p.unit_price}</td><td className="p-2 text-gray-500">/{p.unit}</td>
                        <td className="p-2 text-center"><button onClick={async () => { await axios.delete(`${API}/internal/prices/${encodeURIComponent(p.name)}`); loadPrices(); }} className="text-red-500 hover:text-red-700"><Trash2 className="w-3 h-3" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-2 flex-wrap">
                <input value={newPrice.name} onChange={e => setNewPrice(p => ({ ...p, name: e.target.value }))} placeholder="Item name" className="flex-1 min-w-[120px] px-3 py-2 border rounded text-sm" />
                <input value={newPrice.category} onChange={e => setNewPrice(p => ({ ...p, category: e.target.value }))} placeholder="Category" className="w-24 px-3 py-2 border rounded text-sm" />
                <select value={newPrice.region} onChange={e => setNewPrice(p => ({ ...p, region: e.target.value }))} className="w-20 px-2 py-2 border rounded text-sm">
                  <option value="">All</option><option value="南加">南加</option><option value="北加">北加</option>
                </select>
                <input value={newPrice.unit_price} onChange={e => setNewPrice(p => ({ ...p, unit_price: e.target.value }))} placeholder="Price" type="number" className="w-20 px-3 py-2 border rounded text-sm" />
                <select value={newPrice.unit} onChange={e => setNewPrice(p => ({ ...p, unit: e.target.value }))} className="w-20 px-2 py-2 border rounded text-sm">
                  <option value="each">each</option><option value="sqft">sqft</option><option value="lf">lf</option>
                </select>
                <Button onClick={handleAddPrice} className="bg-red-600 text-white text-sm"><Plus className="w-3 h-3 mr-1" />Add</Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-5 gap-6">
          {/* LEFT: Input Panel */}
          <div className="lg:col-span-2 space-y-4">
            {/* Project Info */}
            <Card className="shadow-md">
              <CardContent className="p-4 space-y-3">
                <h3 className="font-bold text-gray-900 flex items-center gap-2"><FileText className="w-4 h-4" /> Project Info</h3>
                <input value={projectName} onChange={e => setProjectName(e.target.value)} placeholder="Project name" className="w-full px-3 py-2 border rounded-lg text-sm focus:border-red-600 outline-none" />
                <input value={projectAddress} onChange={e => handleAddressChange(e.target.value)} placeholder="Project address" className="w-full px-3 py-2 border rounded-lg text-sm focus:border-red-600 outline-none" />
                {detectedRegion && <div className="flex items-center gap-2"><span className={`inline-block text-xs font-bold px-2 py-0.5 rounded-full ${detectedRegion === '北加' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>{detectedRegion === '北加' ? 'NorCal / 北加' : 'SoCal / 南加'}</span></div>}
                <div className="grid grid-cols-2 gap-2">
                  <input value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Client name" className="px-3 py-2 border rounded-lg text-sm focus:border-red-600 outline-none" />
                  <input value={clientPhone} onChange={e => setClientPhone(e.target.value)} placeholder="Client phone" className="px-3 py-2 border rounded-lg text-sm focus:border-red-600 outline-none" />
                </div>
              </CardContent>
            </Card>

            {/* Voice Input */}
            <Card className="shadow-md">
              <CardContent className="p-4">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2"><Mic className="w-4 h-4" /> Voice / Text Input</h3>
                <div className="flex gap-2 mb-3">
                  <Button onClick={recording ? stopRecording : startRecording} disabled={transcribing}
                    className={`px-4 py-3 font-bold ${recording ? 'bg-red-600 hover:bg-red-700 animate-pulse' : 'bg-gray-900 hover:bg-gray-800'} text-white`}>
                    {recording ? <><MicOff className="w-5 h-5 mr-2" />Stop</> : transcribing ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing...</> : <><Mic className="w-5 h-5 mr-2" />Record</>}
                  </Button>
                </div>
                <textarea value={inputText} onChange={e => setInputText(e.target.value)}
                  placeholder="Speak or type: e.g. '1000 sqft Smart Luxury ADU, add 200A panel upgrade, tile flooring, FOTILE range hood'"
                  rows={5} className="w-full px-3 py-2 border rounded-lg text-sm focus:border-red-600 outline-none resize-none" data-testid="quote-text-input" />
                <Button onClick={handleParse} disabled={parsing} className="w-full mt-2 bg-red-600 hover:bg-red-700 text-white font-bold py-3" data-testid="parse-quote-btn">
                  {parsing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />AI Parsing...</> : <><Send className="w-4 h-4 mr-2" />Generate Quote</>}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* RIGHT: Quote Table */}
          <div className="lg:col-span-3">
            <Card className="shadow-md">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-gray-900 flex items-center gap-2"><Edit3 className="w-4 h-4" /> Quote Items</h3>
                  <div className="flex gap-2">
                    <Button onClick={addItem} variant="outline" className="text-sm border-gray-300"><Plus className="w-3 h-3 mr-1" />Add Item</Button>
                    <Button onClick={handlePdf} disabled={items.length === 0} className="bg-red-600 text-white text-sm"><Download className="w-3 h-3 mr-1" />PDF</Button>
                  </div>
                </div>

                {items.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <Mic className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Speak or type a project description, then click "Generate Quote"</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50"><tr>
                        <th className="p-2 text-left w-[30%]">Item</th>
                        <th className="p-2 text-right w-[15%]">Unit Price</th>
                        <th className="p-2 text-right w-[12%]">Qty</th>
                        <th className="p-2 text-left w-[10%]">Unit</th>
                        <th className="p-2 text-right w-[18%]">Subtotal</th>
                        <th className="p-2 w-[5%]"></th>
                      </tr></thead>
                      <tbody className="divide-y">
                        {items.map(it => (
                          <tr key={it.id} className={`hover:bg-gray-50 ${!it.is_standard ? 'bg-amber-50' : ''}`}>
                            <td className="p-2">
                              <input value={it.name} onChange={e => updateItem(it.id, 'name', e.target.value)}
                                className="w-full px-1 py-0.5 border-b border-transparent hover:border-gray-300 focus:border-red-600 outline-none text-sm font-medium" />
                              <input value={it.description || ''} onChange={e => updateItem(it.id, 'description', e.target.value)}
                                className="w-full px-1 py-0.5 border-b border-transparent hover:border-gray-200 focus:border-red-400 outline-none text-xs text-gray-400" placeholder="Description" />
                            </td>
                            <td className="p-2"><input value={it.unit_price} onChange={e => updateItem(it.id, 'unit_price', parseFloat(e.target.value) || 0)} type="number"
                              className="w-full text-right px-1 py-0.5 border-b border-transparent hover:border-gray-300 focus:border-red-600 outline-none font-medium" /></td>
                            <td className="p-2"><input value={it.quantity} onChange={e => updateItem(it.id, 'quantity', parseFloat(e.target.value) || 0)} type="number"
                              className="w-full text-right px-1 py-0.5 border-b border-transparent hover:border-gray-300 focus:border-red-600 outline-none" /></td>
                            <td className="p-2"><select value={it.unit || 'each'} onChange={e => updateItem(it.id, 'unit', e.target.value)}
                              className="px-1 py-0.5 border-b border-transparent text-xs text-gray-500"><option value="each">each</option><option value="sqft">sqft</option><option value="lf">lf</option></select></td>
                            <td className="p-2 text-right font-bold">${(parseFloat(it.subtotal) || 0).toLocaleString()}</td>
                            <td className="p-2"><button onClick={() => removeItem(it.id)} className="text-gray-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button></td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot><tr className="bg-gray-900 text-white">
                        <td colSpan={4} className="p-3 font-bold text-right">TOTAL</td>
                        <td className="p-3 text-right text-xl font-black">${total.toLocaleString()}</td>
                        <td></td>
                      </tr></tfoot>
                    </table>
                  </div>
                )}

                {modifications.length > 0 && (
                  <div className="mt-3 p-2 bg-amber-50 rounded text-xs text-amber-700">
                    <p className="font-bold mb-1">Modifications ({modifications.length}):</p>
                    {modifications.slice(-5).map((m, i) => (
                      <p key={i}>{m.item}: {m.field} changed from "{m.original}" to "{m.modified}"</p>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Printable Invoice - Matches reference 320-7th.pdf exactly */}
            <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
              <div ref={printRef} style={{ width: '800px', background: '#fff', fontFamily: 'Arial, Helvetica, sans-serif', color: '#000', padding: '0' }}>

                {/* === TOP HEADER: Company info LEFT, Logo RIGHT === */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '40px 48px 20px' }}>
                  {/* Left: Company details */}
                  <div style={{ fontSize: '10px', color: '#333', lineHeight: 1.8 }}>
                    <div style={{ fontWeight: 700, fontSize: '14px', color: '#000', marginBottom: '2px' }}>ALIGCUS Construction Inc</div>
                    <div>1841 S San Gabriel Blvd, Suite D</div>
                    <div>San Gabriel, CA 91776</div>
                    <div>(626) 803-8000</div>
                    <div>office@aligcus.com</div>
                    <div>www.aligcus.com</div>
                  </div>
                  {/* Right: Logos */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <img src="/api/uploads/8bb5e435c484.png" alt="ALIGCUS" style={{ height: '72px' }} crossOrigin="anonymous" />
                  </div>
                </div>

                {/* === INVOICE TITLE + DETAILS ROW === */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '16px 48px 8px' }}>
                  <div style={{ fontSize: '26px', fontWeight: 700, letterSpacing: '3px', color: '#000' }}>INVOICE</div>
                  <table style={{ borderCollapse: 'collapse', fontSize: '10px' }}><tbody>
                    <tr><td style={{ padding: '3px 20px 3px 0', fontWeight: 700, color: '#000' }}>INVOICE #</td><td style={{ padding: '3px 0' }}>{String(Date.now()).slice(-5)}</td></tr>
                    <tr><td style={{ padding: '3px 20px 3px 0', fontWeight: 700, color: '#000' }}>DATE</td><td style={{ padding: '3px 0' }}>{new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}</td></tr>
                    <tr><td style={{ padding: '3px 20px 3px 0', fontWeight: 700, color: '#000' }}>TERMS</td><td style={{ padding: '3px 0' }}>Due on receipt</td></tr>
                    <tr><td style={{ padding: '3px 20px 3px 0', fontWeight: 700, color: '#000' }}>DUE DATE</td><td style={{ padding: '3px 0' }}>{new Date(Date.now() + 30*86400000).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}</td></tr>
                  </tbody></table>
                </div>

                {/* === BILL TO === */}
                <div style={{ padding: '12px 48px 20px' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: '#000', marginBottom: '4px' }}>BILL TO</div>
                  <div style={{ fontSize: '11px', color: '#333', lineHeight: 1.6 }}>
                    {clientName && <div>{clientName}</div>}
                    {projectAddress && <div>{projectAddress}</div>}
                    {clientPhone && <div>{clientPhone}</div>}
                    {projectName && <div style={{ fontWeight: 600, marginTop: '4px' }}>Project: {projectName}</div>}
                  </div>
                </div>

                {/* === ITEMS TABLE === */}
                <div style={{ padding: '0 48px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                    <thead>
                      <tr style={{ borderTop: '1px solid #ccc', borderBottom: '1px solid #ccc' }}>
                        <th style={{ padding: '8px 6px', textAlign: 'left', fontWeight: 700, color: '#000' }}>DESCRIPTION</th>
                        <th style={{ padding: '8px 6px', textAlign: 'center', fontWeight: 700, color: '#000', width: '55px' }}>QTY</th>
                        <th style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 700, color: '#000', width: '85px' }}>RATE</th>
                        <th style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 700, color: '#000', width: '95px' }}>AMOUNT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '8px 6px', verticalAlign: 'top' }}>
                            <div>{it.name}</div>
                            {it.description && <div style={{ fontSize: '9px', color: '#888', marginTop: '1px' }}>{it.description}</div>}
                          </td>
                          <td style={{ padding: '8px 6px', textAlign: 'center', verticalAlign: 'top' }}>{it.quantity}{it.unit !== 'each' ? ` ${it.unit}` : ''}</td>
                          <td style={{ padding: '8px 6px', textAlign: 'right', verticalAlign: 'top' }}>{(parseFloat(it.unit_price) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td style={{ padding: '8px 6px', textAlign: 'right', verticalAlign: 'top' }}>{(parseFloat(it.subtotal) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* === TOTALS - Right aligned === */}
                <div style={{ padding: '16px 48px 20px', display: 'flex', justifyContent: 'flex-end' }}>
                  <table style={{ borderCollapse: 'collapse', fontSize: '11px', width: '240px' }}><tbody>
                    <tr style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '6px 12px 6px 0', textAlign: 'right' }}>SUBTOTAL</td>
                      <td style={{ padding: '6px 0', textAlign: 'right', width: '100px' }}>${total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '6px 12px 6px 0', textAlign: 'right' }}>TAX</td>
                      <td style={{ padding: '6px 0', textAlign: 'right' }}>$0.00</td>
                    </tr>
                    <tr style={{ borderBottom: '2px solid #000' }}>
                      <td style={{ padding: '6px 12px 6px 0', textAlign: 'right', fontWeight: 700 }}>TOTAL</td>
                      <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 700 }}>${total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px 12px 10px 0', textAlign: 'right', fontWeight: 700, fontSize: '13px' }}>BALANCE DUE</td>
                      <td style={{ padding: '10px 0', textAlign: 'right', fontWeight: 900, fontSize: '18px' }}>${total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  </tbody></table>
                </div>

                {/* === NOTES === */}
                <div style={{ padding: '0 48px 16px' }}>
                  <div style={{ fontSize: '9px', color: '#999', lineHeight: 1.8, borderTop: '1px solid #eee', paddingTop: '12px' }}>
                    {notes && <div style={{ color: '#555', marginBottom: '4px' }}>{notes}</div>}
                    <div>&#8226; Estimate only. Final price varies by site conditions.</div>
                    <div>&#8226; Excludes permit fees, utility connections, site prep.</div>
                    <div>&#8226; Valid for 30 days from the date of this quote.</div>
                  </div>
                </div>

                {/* === FOOTER === */}
                <div style={{ padding: '8px 48px 32px', fontSize: '9px', color: '#bbb', textAlign: 'center' }}>Page 1 of 1</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InternalQuote;
